import BigNumber from 'bignumber.js'

const MILLION = new BigNumber('1e6')
const BILLION = new BigNumber('1e9')
const ZERO = new BigNumber(0)

// 将输入统一转成去首尾空格的字符串，空值返回空字符串。
const toText = (value) => value?.toString?.()?.trim?.() ?? ''

// 将输入解析为 BigNumber，非法数字或空值时返回 null。
const getBigNumber = (value) => {
  const rawText = toText(value)
  if (rawText === '') {
    return null
  }

  const bigNumber = new BigNumber(rawText)
  return bigNumber.isNaN() || !bigNumber.isFinite() ? null : bigNumber
}

// 获取输入的小数位数；科学计数法会先展开再统计。
const getFractionLength = (value) => {
  const rawText = toText(value)
  if (rawText === '') {
    return 0
  }

  const bigNumber = getBigNumber(rawText)
  const normalizedText = bigNumber && /e/i.test(rawText) ? bigNumber.toFixed() : rawText
  return normalizedText.split('.')[1]?.length ?? 0
}

// 按分隔符拆分字符串并返回指定位置，取不到时返回空字符串。
const st = (s, index, f = '/') => {
  if (isEmpty(s)) return ''
  return s.split(f)[index] ?? ''
}

// 清洗非负数字输入：仅保留数字和一个小数点。
const matchPositiveNum = (origin) => {
  return origin.replace(/^\./, '').replace(/[^0-9\.]/g, '').replace(/^-/, '').replace(/(\..*)\./, '$1').replace(/^0+/, '0')
}

// 清洗可带负号的数字输入：仅保留数字、负号和一个小数点。
const matchNum = (origin) => {
  return origin.replace(/^\./, '').replace(/[^0-9\.\-]/g, '').replace(/(\..*)\./, '$1').replace(/^0+/, '0')
}

// 输入过程中的数字清洗：仅保留数字和单个小数点，支持以 '.' 开头，并规范整数部分前导 0。
const inputNum = (vs) => {
  let value = toText(vs)
  if (value === '') return ''

  value = value.replace(/[^\d.]/g, '')
  if (value === '') return ''

  if (value.startsWith('.')) {
    value = `0${value}`
  }

  const firstDotIndex = value.indexOf('.')
  if (firstDotIndex !== -1) {
    value = `${value.slice(0, firstDotIndex + 1)}${value.slice(firstDotIndex + 1).replace(/\./g, '')}`
  }

  const hasDot = value.includes('.')
  const [integerPart = '0', fractionalPart = ''] = value.split('.')
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '') || '0'

  if (hasDot) {
    return `${normalizedInteger || '0'}.${fractionalPart}`
  }

  return normalizedInteger
}

// 按指定小数位截断并补 0，不加千分位；支持科学计数法输入。
const toFixed = (v, lan) => {
  try {
    const rawText = toText(v)
    if (isEmpty(rawText)) {
      return null
    }

    const value = getBigNumber(rawText)
    if (!value) {
      return ''
    }

    const decimalPlaces = Number(lan) || 0
    if (decimalPlaces === 0 && value.isZero()) {
      return 0
    }

    return value.decimalPlaces(decimalPlaces, BigNumber.ROUND_DOWN).toFixed(decimalPlaces)
  } catch (e) {
    return ''
  }
}

// 统一数字展示：支持字符串科学计数法，非数字原样返回，并按原始小数位补 0 + 加千分位。
const numFormat = (testv = '') => {
  const rawText = toText(testv)
  if (rawText === '') {
    return ''
  }

  const value = getBigNumber(rawText)
  if (!value) {
    return rawText
  }

  return value.toFormat(getFractionLength(rawText))
}

// 大额数字简写展示：按数量级转换为 M / B，并保留原始小数位后再格式化输出。
const amountNumFormat = (text) => {
  const rawText = toText(text)
  if (rawText === '') {
    return ''
  }

  const value = getBigNumber(rawText)
  if (!value) {
    return rawText
  }

  const absoluteValue = value.abs()
  const fractionalLen = getFractionLength(rawText)
  if (absoluteValue.isGreaterThanOrEqualTo(BILLION)) {
    return `${numFormat(toFixed(value.dividedBy(BILLION), fractionalLen))}B`
  }

  if (absoluteValue.isGreaterThanOrEqualTo(MILLION)) {
    return `${numFormat(toFixed(value.dividedBy(MILLION), fractionalLen))}M`
  }

  return numFormat(toFixed(value, fractionalLen))
}

// 将字符串首字母转成大写，异常时返回空字符串。
const toStartUpperCase = str => {
  try {
    return str[0].toUpperCase() + str.slice(1)
  } catch (e) {
    return ''
  }
}

// 根据 show 决定是否掩码显示文本，默认按原文本长度生成星号。
const coverText = (val = '', show, length) => {
  const str = val.toString()
  length = length ?? str.length
  return show ? str : '*'.repeat(length)
}

// 根据数值正负返回对应的涨跌颜色 class，0 使用传入默认类名。
const priceClass = (v, cls = 'text-9b!') => {
  if (isEmpty(v)) return cls
  const value = getBigNumber(v)
  if (!value) return cls
  if (value.isZero()) return cls
  return value.isGreaterThan(ZERO) ? 'c-buy' : 'c-sell'
}

// 根据数值正负返回 tab 颜色 class。
const priceTabClass = v => {
  if (isEmpty(v)) return ''
  const value = getBigNumber(v)
  if (!value) return ''
  if (value.isZero()) return 'tab-info'
  return value.isGreaterThan(ZERO) ? 'tab-buy' : 'tab-sell'
}

// 按涨跌值补充正负号并保留两位小数；空值或非法值沿用历史行为返回 'C'。
const pnFill = (v = 0, s) => {
  if (!isEmpty(s) && /undefined/.test(s)) {
    return '-'
  }

  if (isEmpty(v)) {
    return 'C'
  }

  const value = getBigNumber(v)
  if (!value) {
    return 'C'
  }

  const prefix = value.isGreaterThan(ZERO) ? '+' : value.isLessThan(ZERO) ? '-' : ''
  return `${prefix}${toFixed(value.abs(), 2)}`
}

// 比例转百分比展示：0.2 -> 20%，支持按需给正数补 + 号。
const formatPercent = (value, { prefix = false } = {}) => {
  const ratio = getBigNumber(value)
  if (!ratio) {
    return '--'
  }

  const percent = ratio.multipliedBy(100)
  const formattedValue = percent.decimalPlaces(2, BigNumber.ROUND_DOWN).toString()
  const sign = prefix && percent.isGreaterThan(ZERO) ? '+' : ''

  return `${sign}${numFormat(formattedValue)}%`
}

// 手机号脱敏显示，不符合手机号格式时返回空字符串。
const hidePhone = (v) => {
  if (!$v.re.phone.test(v)) {
    // throw new Error('非电话')
    return ''
  }
  return `${v.substr(0, 3)}****${v.substr(-4)}`
}

// 邮箱脱敏显示，不符合邮箱格式时返回空字符串。
const hideEmail = (v) => {
  if (!$v.re.email.test(v)) {
    // throw new Error('非电话')
    return ''
  }
  return `${v.substr(0, 2)}****@***.com`
}

// 获取当前模块下静态资源的绝对访问地址。
const getAssetsFile = (url) => {
  return new URL(url, import.meta.url).href
}

// 按指定小数位数截断字符串，不补 0。
const keepDot = (str, length) => {
  if (!length) return str
  if (!str) return str
  const text = str.toString()
  const [front, end] = text.split('.')
  return end && end.length > +length ? `${front}.${end.slice(0, +length)}` : text
}

// 将小数转换为百分比，截断保留最多2位小数并去除多余的零；传入 decimalPlaces = null 时代表不限制精度并展示完整位数
const formatRate = (v, decimalPlaces = 2, roundingMode = BigNumber.ROUND_DOWN) => {
  const rawText = toText(v)
  if (rawText === '') {
    return '--'
  }
  const value = getBigNumber(rawText)
  if (!value) {
    return '--'
  }
  let rate = value.times(100)
  if (decimalPlaces !== null && decimalPlaces !== undefined) {
    rate = rate.decimalPlaces(Number(decimalPlaces), roundingMode)
  }
  const fixedStr = (decimalPlaces !== null && decimalPlaces !== undefined)
    ? rate.toFixed(Number(decimalPlaces))
    : rate.toFixed()
  const trimmedStr = fixedStr.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
  return `${trimmedStr}%`
}

// 检查并格式化空值，空值时使用默认值兜底
const emptyFallback = (v, defaultVal = '--') => {
  return !isEmpty(v) ? v : defaultVal
}

// 格式化美元金额，带 $ 前缀，千分位，并进行 '--' 空值兜底，支持 signed 显示正号，以及 decimalPlaces 指定保留小数位数
const formatUsd = (value, signed = false, decimalPlaces = null) => {
  const num = getBigNumber(value)
  if (num === null) return '--'
  const sign = signed && num.gt(0) ? '+' : ''
  const formattedVal = (decimalPlaces !== null && decimalPlaces !== undefined)
    ? num.decimalPlaces(Number(decimalPlaces), BigNumber.ROUND_DOWN).toFixed(decimalPlaces)
    : value
  return `${sign}$${numFormat(formattedVal)}`
}

// 判断输入是否为有效且有限的数值
const isValidNumber = (value) => {
  return getBigNumber(value) !== null
}

export default {
  st,
  getFractionLength,
  numFormat,
  inputNum,
  amountNumFormat,
  priceClass,
  priceTabClass,
  toFixed,
  toStartUpperCase,
  coverText,
  pnFill,
  formatPercent,
  hidePhone,
  hideEmail,
  getAssetsFile,
  keepDot,
  matchPositiveNum,
  matchNum,
  formatRate,
  emptyFallback,
  formatUsd,
  isValidNumber,
}
